import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { toast, ToastContainer } from "react-toastify";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import "react-toastify/dist/ReactToastify.css";
import {
  apiUrl,
  apiUrlDev,
  apiUrlLocal,
  apiUrlProdFirebase,
} from "../../globalVariables";
import { palette } from "./theme";
import MinimumDocumentsPanel, {
  DOC_REQUIREMENTS,
  DOC_REQUIREMENTS_KEY,
  MAX_MINIMUM_COUNT,
} from "./MinimumDocumentsPanel";
import GlobalOcrSwitchPanel from "./GlobalOcrSwitchPanel";

const ADDY_APP_URL_PROD = "https://app.addy.so";
const ADDY_APP_URL_DEV = "https://devmail.addy.so";

const OCR_KEY = "ocrClassificationPerOrgPerPeriod";
const PC_KEY = "pcAutoRunPerOrgPerPeriod";
// Per-loan lifetime cap. Unlike the other two this has no period and no org-level
// usage counter — consumption is tracked on each loan document — so it never
// feeds the KPI or severity calculations on this page.
const PC_PER_LOAN_KEY = "pcAutoRunPerLoanLifetime";
// Org-level boolean, not part of the aiUsageLimits map: the numeric override is
// validated as a positive integer, so "no cap" needs its own field.
const PC_PER_LOAN_UNLIMITED_KEY = "disableAutoRunPerLoanLimit";

const OCR_USAGE_KEY = "ocr_classification";
const PC_USAGE_KEY = "checklist_run";

// Which OCR engine extracts an org's documents. Mirrors the backend's
// `ocrRouting.mode` (see functions/lib/addyOcrModes.js) — keep in sync.
const OCR_MODE_EXTEND = "extend";
const OCR_MODE_PRIMARY = "primary";
const OCR_MODE_SHADOW = "shadow";

// Ordered most-conservative first; this is the order shown in the drawer.
const OCR_MODE_OPTIONS = [
  {
    id: OCR_MODE_EXTEND,
    label: "Extend only",
    short: "Extend",
    color: "#94a3b8",
    description:
      "Extend.ai extracts every document. The default for all organizations.",
  },
  {
    id: OCR_MODE_PRIMARY,
    label: "Addy OCR (primary)",
    short: "Addy primary",
    color: "#0891b2",
    description:
      "Addy OCR runs first. Extend is only called if Addy OCR fails, so a healthy org never pays for Extend.",
  },
  {
    id: OCR_MODE_SHADOW,
    label: "Shadow (run both)",
    short: "Shadow",
    color: "#7c3aed",
    description:
      "Both engines run in parallel and results are stored side by side. Costs both engines per document — use for quality audits, not steady state.",
  },
];

const OCR_MODE_BY_ID = OCR_MODE_OPTIONS.reduce((acc, option) => {
  acc[option.id] = option;
  return acc;
}, {});

/**
 * The routing mode the org actually runs. The API resolves it with the same
 * code that routes documents (saved value, else the rollout default, after the
 * environment guards), so an org with nothing saved shows what it really gets.
 * Older API versions only send the saved value.
 */
function ocrModeOf(org) {
  const raw = String(org?.ocrRoutingResolved || org?.ocrRouting?.mode || "").toLowerCase();

  return OCR_MODE_BY_ID[raw] ? raw : OCR_MODE_EXTEND;
}

// --- Engine within the routing mode: Performance or Light ------------------
// On Extend it is `extendAIEngine` (parse_performance / parse_light); on Addy
// OCR it is `addyOcrEngine` (addyai-performance / addyai-lite). The section
// follows the mode chosen above and only ever saves the field for that mode.
const ENGINE_PERFORMANCE = "performance";
const ENGINE_LIGHT = "light";
const ENGINE_COPY = {
  extend: {
    label: "Extend engine",
    help: "Which Extend engine parses this organization's documents.",
    performance: "Extend's highest-accuracy engine.",
    light: "Extend's cheaper, faster engine; tables come back as markdown.",
  },
  addy: {
    label: "Addy Intelligence engine",
    help: "Which Addy engine reads this organization's documents.",
    performance: "MinerU fine-tune: one model for layout and reading.",
    light: "Addy's own layout model with the 0.9B reader.",
  },
};
const EXTEND_ENGINE_FOR = {
  [ENGINE_PERFORMANCE]: "parse_performance",
  [ENGINE_LIGHT]: "parse_light",
};
const ADDY_ENGINE_FOR = {
  [ENGINE_PERFORMANCE]: "addyai-performance",
  [ENGINE_LIGHT]: "addyai-lite",
};
const ENGINE_FOR_STORED = {
  parse_performance: ENGINE_PERFORMANCE,
  parse_light: ENGINE_LIGHT,
  "addyai-performance": ENGINE_PERFORMANCE,
  "addyai-lite": ENGINE_LIGHT,
};

/** "extend" or "addy": which engine family a routing mode uses. */
function engineFamilyOf(mode) {
  return mode === OCR_MODE_EXTEND ? "extend" : "addy";
}

/** The engine the org actually runs within the given routing mode. */
function engineOf(org, mode) {
  if (engineFamilyOf(mode) === "extend") {
    return (
      ENGINE_FOR_STORED[String(org?.extendAIEngine || org?.extendAIEngineDefault || "")] ||
      ENGINE_PERFORMANCE
    );
  }

  return (
    ENGINE_FOR_STORED[String(org?.addyOcrEngineResolved || org?.addyOcrEngine || org?.addyOcrSplitEngine || "")] ||
    ENGINE_LIGHT
  );
}

// Big page size on purpose. With ~22k total orgs, 1k per click gets us to a
// meaningful KPI sample fast, and to a complete scan in ~22 clicks if needed.
// Backend caps at PAGE_SIZE_MAX (currently 1000) so don't bump above that
// without changing the cap there too.
const PAGE_SIZE = 1000;

// Group ordering matters — most urgent first. Each org lands in exactly one
// bucket using the same precedence as this list.
const GROUPS = [
  {
    id: "critical",
    label: "At cap",
    description: "Hit the limit on at least one metric this period.",
    color: "#ef4444",
    soft: "rgba(239, 68, 68, 0.10)",
  },
  {
    id: "watch",
    label: "Watch list",
    description: "Used 70–89% of the limit on at least one metric.",
    color: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.10)",
  },
  {
    id: "overrides",
    label: "Custom overrides",
    description: "Has a per-org limit configured.",
    color: "#7c3aed",
    soft: "rgba(124, 58, 237, 0.10)",
  },
  {
    id: "active",
    label: "Active auto-run",
    description: "Auto-run on, healthy usage.",
    color: "#10b981",
    soft: "rgba(16, 185, 129, 0.10)",
  },
  {
    id: "splitting",
    label: "Document splitting",
    description: "Post-classification split flow is enabled.",
    color: "#2563eb",
    soft: "rgba(37, 99, 235, 0.10)",
  },
  {
    id: "ocrPrimary",
    label: "Addy OCR primary",
    description: "Addy OCR extracts documents; Extend is the fallback only.",
    color: "#0891b2",
    soft: "rgba(8, 145, 178, 0.10)",
  },
  {
    id: "ocrShadow",
    label: "OCR shadow",
    description: "Addy OCR and Extend both run on every document.",
    color: "#7c3aed",
    soft: "rgba(124, 58, 237, 0.10)",
  },
  {
    id: "quiet",
    label: "Quiet",
    description: "No auto-run, no overrides, no recent usage.",
    color: "#94a3b8",
    soft: "rgba(148, 163, 184, 0.10)",
  },
];

// Quieter palette than v1: page bg matches card bg more closely so cards
// "float" rather than sit on a contrasting tray; hairline borders carry the
// structure instead of shadows.
// All styles live here so the JSX stays readable. Functions encode the dynamic
// bits (severity colors, dirty/active states) without a CSS preprocessor.
const sx = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #f7f8fb 0%, #fbfcfe 360px, #f6f7fb 100%)",
    color: palette.text,
    width: "100%",
    boxSizing: "border-box",
    padding: "24px 28px 64px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 0,
    lineHeight: 1.15,
  },
  subtitle: {
    margin: "6px 0 0",
    fontSize: 12,
    color: palette.textMuted,
    maxWidth: 640,
    lineHeight: 1.45,
  },
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
  },
  envSegment: {
    display: "inline-flex",
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    padding: 4,
    boxShadow: palette.shadow,
  },
  envButton: (active) => ({
    appearance: "none",
    border: "none",
    background: active ? palette.accent : "transparent",
    color: active ? "#fff" : palette.textMuted,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
    transition: "background 0.15s ease",
  }),
  refreshBtn: {
    appearance: "none",
    border: `1px solid ${palette.border}`,
    background: palette.surface,
    color: palette.text,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    boxShadow: palette.shadow,
  },
  buttonIcon: {
    fontSize: 16,
  },
  envBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "3px 9px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    color: palette.accent,
    background: palette.accentSoft,
    marginLeft: 8,
    verticalAlign: "middle",
  },

  // KPI hero strip — kept as filterable summary, but smaller numbers and
  // tighter padding so it doesn't dominate the viewport.
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  kpiCard: (active, color) => ({
    appearance: "none",
    textAlign: "left",
    background: active
      ? `linear-gradient(180deg, ${color}12 0%, ${palette.surface} 100%)`
      : `linear-gradient(180deg, ${palette.surface} 0%, ${palette.surfaceTint} 100%)`,
    border: `1px solid ${active ? color : palette.border}`,
    borderTop: `3px solid ${active ? color : "transparent"}`,
    borderRadius: 8,
    padding: "12px 14px 13px",
    cursor: "pointer",
    boxShadow: active ? `0 10px 22px ${color}16` : palette.shadow,
    transition:
      "background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    minHeight: 92,
    minWidth: 0,
  }),
  kpiLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: palette.textMuted,
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
  },
  kpiDot: (color) => ({
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: color,
  }),
  kpiValue: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: 0,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.05,
    color: palette.text,
    display: "flex",
    alignItems: "baseline",
    gap: 4,
  },
  // Faded "+" suffix when the KPI is computed from a partial scan. Smaller
  // than the number so the eye reads "12-ish" rather than "12 plus".
  kpiPartialBadge: {
    fontSize: 14,
    fontWeight: 600,
    color: palette.textDim,
    letterSpacing: 0,
  },
  kpiSubtext: {
    fontSize: 11,
    color: palette.textDim,
    display: "flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.35,
  },

  globalDefaultsPanel: {
    background: `linear-gradient(180deg, ${palette.surface} 0%, ${palette.surfaceTint} 100%)`,
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 18,
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
  },
  globalDefaultsHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },
  globalDefaultsEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: palette.accent,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 5,
  },
  globalDefaultsTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: palette.text,
    letterSpacing: "-0.01em",
  },
  globalDefaultsHelp: {
    margin: "4px 0 0",
    fontSize: 12,
    color: palette.textMuted,
    lineHeight: 1.4,
  },
  globalDefaultsBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 9px",
    borderRadius: 999,
    background: palette.trackSoft,
    border: `1px solid ${palette.border}`,
    color: palette.textMuted,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  globalDefaultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 12,
    alignItems: "start",
  },
  globalDefaultTile: (color) => ({
    minWidth: 0,
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderLeft: `4px solid ${color}`,
    borderRadius: 10,
    padding: "13px 14px",
  }),
  globalDefaultTileTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 10,
  },
  globalDefaultLabel: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: palette.text,
  },
  globalDefaultCaption: {
    margin: "3px 0 0",
    fontSize: 11,
    color: palette.textMuted,
    lineHeight: 1.35,
  },
  globalDefaultValuePreview: {
    fontSize: 22,
    fontWeight: 800,
    color: palette.text,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  globalDefaultInputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  globalDefaultUnit: {
    fontSize: 12,
    color: palette.textMuted,
    whiteSpace: "nowrap",
  },
  globalDefaultActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${palette.border}`,
  },
  metaText: {
    fontSize: 11,
    color: palette.textDim,
    marginRight: "auto",
  },

  // Search bar (top)
  searchRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginBottom: 18,
  },
  searchWrap: {
    flex: "1 1 320px",
    position: "relative",
    minWidth: 280,
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 36px 11px 38px",
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    fontSize: 14,
    color: palette.text,
    background: palette.surface,
    outline: "none",
    boxShadow: palette.shadow,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: palette.textDim,
    fontSize: 18,
    pointerEvents: "none",
  },
  searchClear: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: palette.textMuted,
    cursor: "pointer",
    width: 24,
    height: 24,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  countText: {
    fontSize: 12,
    color: palette.textDim,
    marginLeft: "auto",
    fontVariantNumeric: "tabular-nums",
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    flexWrap: "wrap",
  },
  // Hints next to the count: amber while scanning, green when complete.
  scanHint: {
    color: palette.warn,
    fontWeight: 500,
  },
  scanHintDone: {
    color: palette.ok,
    fontWeight: 500,
  },
  spinnerDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: palette.accent,
    marginLeft: 8,
    animation: "olPulse 1s ease-in-out infinite",
  },

  // Section header — Linear/Vercel style: typography carries the hierarchy,
  // a small colored mark is the only color signal, count sits as a neutral
  // chip on the right. No tinted backgrounds, no colored borders.
  sectionHeader: () => ({
    display: "grid",
    gridTemplateColumns: "10px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: 10,
    margin: "30px 0 12px",
    paddingBottom: 11,
    borderBottom: `1px solid ${palette.border}`,
  }),
  sectionMark: (color) => ({
    width: 10,
    height: 10,
    borderRadius: 3,
    background: color,
    flexShrink: 0,
  }),
  sectionText: {
    minWidth: 0,
  },
  sectionTitle: {
    display: "block",
    fontSize: 15,
    fontWeight: 700,
    color: palette.text,
    letterSpacing: 0,
    lineHeight: 1.25,
  },
  sectionDescription: {
    display: "block",
    fontSize: 12,
    color: palette.textMuted,
    fontWeight: 400,
    lineHeight: 1.4,
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sectionCount: {
    fontSize: 12,
    fontWeight: 600,
    color: palette.textMuted,
    fontVariantNumeric: "tabular-nums",
    background: palette.track,
    padding: "4px 9px",
    borderRadius: 6,
    minWidth: 28,
    textAlign: "center",
  },

  // Org cards — quiet, consistent cards. Status gets one strong visual affordance:
  // a slim left rail. Everything else stays neutral so the list feels like an
  // admin surface, not a pile of alerts.
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 370px), 1fr))",
    gap: 12,
  },
  card: (color) => ({
    position: "relative",
    overflow: "hidden",
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderLeft: `4px solid ${color || palette.idle}`,
    borderRadius: 10,
    padding: "14px 15px",
    cursor: "pointer",
    transition: "border-color 0.12s ease, box-shadow 0.12s ease",
    minWidth: 0,
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
  }),
  cardTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 0,
    minWidth: 0,
  },
  cardTitleBlock: {
    minWidth: 0,
    flex: 1,
  },
  cardTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minWidth: 0,
    flex: 1,
  },
  statusDot: (color) => ({
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),
  orgName: {
    fontSize: 15,
    fontWeight: 700,
    color: palette.text,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    lineHeight: 1.25,
  },
  orgMeta: {
    fontSize: 11,
    color: palette.textDim,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: 5,
  },
  cardPills: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  cardTags: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 10,
  },
  // Severity pill — only used for "At cap" / "Watch". Sentence case, tighter,
  // not all-caps shouty. Reserved for state that warrants attention.
  pill: (color, soft) => ({
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 700,
    color,
    background: soft,
    border: `1px solid ${color}22`,
    padding: "3px 8px",
    borderRadius: 6,
    whiteSpace: "nowrap",
    lineHeight: 1.4,
  }),

  // Informational chip — neutral background, small colored dot inside.
  // Linear/Vercel-style. Used for Auto-run, Custom, NewFi where we want to
  // signal a trait without competing with severity.
  chip: () => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 500,
    color: palette.textMuted,
    background: palette.trackSoft,
    border: `1px solid ${palette.border}`,
    padding: "2px 7px 2px 6px",
    borderRadius: 6,
    whiteSpace: "nowrap",
    lineHeight: 1.4,
  }),
  chipDot: (color) => ({
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),

  // Metric row inside card — slim bar + count only. Reset countdown is shown
  // once at the bottom of the card, not per metric.
  metricGrid: {
    display: "grid",
    gap: 10,
    marginTop: 13,
  },
  metric: {
    minWidth: 0,
  },
  metricLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    fontSize: 11,
    color: palette.textMuted,
    marginBottom: 5,
    minWidth: 0,
  },
  metricLabelLeft: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metricCount: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 700,
    color: palette.text,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  metricCountDim: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    color: palette.textDim,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  customMark: {
    fontSize: 10,
    color: palette.newfi,
    fontWeight: 700,
    background: palette.newfiSoft,
    borderRadius: 5,
    padding: "1px 5px",
  },
  progressTrack: {
    width: "100%",
    height: 5,
    background: palette.track,
    borderRadius: 999,
    overflow: "hidden",
  },
  progressFill: (color, pct) => ({
    width: `${Math.max(0, Math.min(100, pct))}%`,
    height: "100%",
    background: color,
    borderRadius: 999,
    transition: "width 0.3s ease",
  }),

  // Card footer — single line, no separator. Hidden if there's nothing
  // interesting to say.
  cardFooter: {
    fontSize: 11,
    color: palette.textDim,
    marginTop: 11,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // Drawer (slide-in from right)
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.55)",
    backdropFilter: "blur(3px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    overflowY: "auto",
    animation: "olFadeIn 0.15s ease",
  },
  modal: {
    width: "min(1080px, 100%)",
    maxHeight: "calc(100vh - 48px)",
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 16,
    boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "olModalIn 0.18s cubic-bezier(0.16, 1, 0.3, 1)",
  },
  modalHeader: {
    padding: "20px 26px 16px",
    borderBottom: `1px solid ${palette.border}`,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  modalTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: palette.text,
  },
  modalSubtitle: {
    margin: "4px 0 0",
    fontSize: 12,
    color: palette.textDim,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  modalClose: {
    appearance: "none",
    border: `1px solid ${palette.border}`,
    background: "#fff",
    color: palette.textMuted,
    cursor: "pointer",
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // "What is in effect" strip: the settings below are spread across two columns,
  // so this keeps the resulting state readable at a glance while editing.
  modalSummary: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: "12px 26px",
    borderBottom: `1px solid ${palette.border}`,
    background: "#f8fafc",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: "7px 12px",
    borderRadius: 8,
    background: "#fff",
    border: `1px solid ${palette.border}`,
    minWidth: 104,
  },
  summaryLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: palette.textDim,
  },
  summaryValue: (color) => ({
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: color || palette.text,
  }),

  modalBody: {
    flex: 1,
    overflowY: "auto",
    padding: "20px 26px 24px",
    background: "#f8fafc",
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 18,
    alignItems: "start",
  },
  modalColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    minWidth: 0,
  },
  settingsCard: {
    background: "#fff",
    border: `1px solid ${palette.border}`,
    borderRadius: 12,
    padding: "0 18px 16px",
  },
  // Child settings that only take effect while their parent toggle is on.
  nestedFields: {
    marginTop: 14,
    marginLeft: 10,
    paddingLeft: 14,
    borderLeft: `2px solid ${palette.border}`,
  },
  nestedFieldsInactive: {
    opacity: 0.55,
  },
  settingsCardHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 0 12px",
    borderBottom: `1px solid ${palette.border}`,
  },
  settingsCardTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: palette.textMuted,
  },
  modalFooter: {
    padding: "14px 26px",
    borderTop: `1px solid ${palette.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "#fbfcfe",
  },

  // Drawer sections
  fieldGroup: {
    padding: "16px 0",
    borderBottom: `1px solid ${palette.border}`,
  },
  fieldGroupLast: {
    padding: "16px 0 0",
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: palette.text,
    margin: "0 0 4px",
  },
  fieldHelp: {
    fontSize: 12,
    color: palette.textMuted,
    margin: "0 0 12px",
    lineHeight: 1.4,
  },

  // OCR engine selector (radio cards)
  ocrModeList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  ocrModeOption: (selected, color) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    cursor: "pointer",
    background: selected ? `${color}0F` : palette.trackSoft,
    border: `1px solid ${selected ? color : palette.border}`,
    transition: "background 120ms ease, border-color 120ms ease",
  }),
  ocrModeRadio: {
    marginTop: 3,
    accentColor: palette.accent,
    cursor: "pointer",
    flexShrink: 0,
  },
  ocrModeLabel: (color) => ({
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color,
    marginBottom: 2,
  }),
  ocrModeDescription: {
    display: "block",
    fontSize: 12,
    color: palette.textMuted,
    lineHeight: 1.4,
  },
  ocrModeWarning: {
    fontSize: 12,
    lineHeight: 1.4,
    color: palette.warn,
    background: palette.warnSoft,
    border: `1px solid ${palette.warn}33`,
    borderRadius: 8,
    padding: "8px 10px",
    margin: "10px 0 0",
  },

  // Auto-run toggle row
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  toggleSwitch: (on) => ({
    position: "relative",
    display: "inline-block",
    width: 44,
    height: 24,
    background: on ? palette.ok : "#cbd5e1",
    borderRadius: 999,
    cursor: "pointer",
    transition: "background 0.15s ease",
    flexShrink: 0,
  }),
  toggleKnob: (on) => ({
    position: "absolute",
    top: 2,
    left: on ? 22 : 2,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.15s ease",
    boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
  }),

  // Big usage block in drawer
  drawerUsageBlock: {
    background: "#f8fafc",
    border: `1px solid ${palette.border}`,
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 12,
  },
  drawerUsageNumbers: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 6,
  },
  drawerUsageBig: {
    fontSize: 22,
    fontWeight: 700,
    color: palette.text,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.02em",
  },
  drawerUsageOf: {
    fontSize: 13,
    color: palette.textMuted,
  },
  drawerUsagePct: (color) => ({
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 700,
    color,
    fontVariantNumeric: "tabular-nums",
  }),
  drawerUsageReset: {
    fontSize: 11,
    color: palette.textDim,
    marginTop: 6,
  },

  // Override input row
  overrideRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  numberInput: {
    flex: 1,
    padding: "9px 12px",
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    fontSize: 14,
    color: palette.text,
    background: "#fff",
    fontVariantNumeric: "tabular-nums",
    outline: "none",
    transition: "border-color 0.12s ease, box-shadow 0.12s ease",
  },
  // Sits inside the requirement's label so the sentence reads as one setting
  // rather than a toggle and an unrelated box.
  inlineCountInput: {
    width: 46,
    padding: "2px 6px",
    border: `1px solid ${palette.borderStrong}`,
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    color: palette.text,
    background: "#fff",
    fontVariantNumeric: "tabular-nums",
    outline: "none",
  },
  inputDirty: {
    borderColor: palette.accent,
    boxShadow: `0 0 0 3px ${palette.accentSoft}`,
  },
  inputError: {
    borderColor: palette.danger,
    boxShadow: `0 0 0 3px ${palette.dangerSoft}`,
  },
  defaultBtn: {
    appearance: "none",
    border: `1px solid ${palette.border}`,
    background: palette.surface,
    color: palette.textMuted,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 8,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  errorText: {
    fontSize: 11,
    color: palette.danger,
    marginTop: 6,
  },

  // Footer buttons
  cancelBtn: {
    appearance: "none",
    border: `1px solid ${palette.border}`,
    background: palette.surface,
    color: palette.text,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    cursor: "pointer",
  },
  saveBtn: (enabled) => ({
    appearance: "none",
    border: "none",
    background: enabled ? palette.accent : "#e2e8f0",
    color: enabled ? "#fff" : palette.textDim,
    padding: "9px 22px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    cursor: enabled ? "pointer" : "not-allowed",
    transition: "background 0.12s ease",
  }),

  // Misc
  loadMoreBtn: (enabled) => ({
    appearance: "none",
    border: `1px solid ${palette.border}`,
    background: enabled ? palette.surface : "#f1f5f9",
    color: enabled ? palette.text : palette.textDim,
    padding: "10px 20px",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    cursor: enabled ? "pointer" : "not-allowed",
    margin: "24px auto 0",
    display: "block",
    boxShadow: enabled ? palette.shadow : "none",
  }),
  empty: {
    padding: "60px 20px",
    textAlign: "center",
    color: palette.textMuted,
    background: palette.surface,
    border: `1px dashed ${palette.borderStrong}`,
    borderRadius: 8,
    boxShadow: palette.shadow,
  },
  loading: {
    padding: "60px 20px",
    textAlign: "center",
    color: palette.textMuted,
    background: palette.surface,
    border: `1px solid ${palette.border}`,
    borderRadius: 8,
    boxShadow: palette.shadow,
  },
  resetFilterBtn: {
    appearance: "none",
    border: `1px solid ${palette.accent}22`,
    background: "#fff",
    color: palette.accent,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    cursor: "pointer",
    marginLeft: 8,
  },
};

// ---------- pure helpers ----------

function buildOrgEditUrl(env, orgId) {
  if (!orgId) return null;
  const base = env === "dev" ? ADDY_APP_URL_DEV : ADDY_APP_URL_PROD;
  return `${base}/organizations/${encodeURIComponent(orgId)}/edit`;
}

function getEnvBackends(env) {
  if (env === "dev") {
    const isLocalhost =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1");
    return isLocalhost ? [apiUrlLocal, apiUrlDev] : [apiUrlDev];
  }
  return [apiUrl, apiUrlProdFirebase];
}

function formatPeriodLabel(quotaWindowMs) {
  if (!quotaWindowMs || !Number.isFinite(quotaWindowMs)) return "per period";
  const hours = quotaWindowMs / (1000 * 60 * 60);
  if (hours >= 1) {
    return hours === 1 ? "per 1h" : `per ${Math.round(hours)}h`;
  }
  const minutes = quotaWindowMs / (1000 * 60);
  return `per ${Math.round(minutes)} min`;
}

// Backend uses lazy reset: count is only zeroed when the next op comes in
// AFTER the window expired. So an org can sit at "10/10" in the DB for days
// even though, semantically, that quota is gone. The dashboard reflects the
// SEMANTIC state — anything where periodEndsAt is in the past behaves as if
// count is 0. Use this everywhere we read currentUsage.
function getEffectiveQuotaCount(usage) {
  if (!usage || typeof usage !== "object") return 0;
  const raw = Number.isFinite(usage.count) ? usage.count : 0;
  const expired = usage.periodEndsAt && Date.now() >= usage.periodEndsAt;
  return expired ? 0 : raw;
}

function formatTimeRemaining(periodEndsAt) {
  if (!periodEndsAt) return "";
  const remainingMs = periodEndsAt - Date.now();
  // Window already passed → backend will lazy-reset on the next op, so there's
  // nothing meaningful to count down to. Hide the chip entirely instead of
  // saying "resets shortly" (which is misleading when the window expired
  // hours/days ago).
  if (remainingMs <= 0) return "";
  const totalMin = Math.floor(remainingMs / (1000 * 60));
  if (totalMin < 1) return "resets <1m";
  if (totalMin < 60) return `resets in ${totalMin}m`;
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return minutes > 0
    ? `resets in ${hours}h ${minutes}m`
    : `resets in ${hours}h`;
}

function formatRelativeTime(ts) {
  if (!ts) return "";
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return "just now";
  const totalMin = Math.floor(diffMs / (1000 * 60));
  if (totalMin < 1) return "just now";
  if (totalMin < 60) return `${totalMin}m ago`;
  const hours = Math.floor(totalMin / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function severityForRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return "idle";
  if (ratio >= 0.9) return "critical";
  if (ratio >= 0.7) return "watch";
  return "ok";
}

function severityColor(sev) {
  switch (sev) {
    case "critical":
      return palette.danger;
    case "watch":
      return palette.warn;
    case "ok":
      return palette.ok;
    default:
      return palette.idle;
  }
}

function parseInputToOverride(raw) {
  const trimmed = String(raw || "").trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
    return { error: "Must be a positive integer" };
  }
  if (n > 100000) return { error: "Max 100,000" };
  return n;
}

// Compute the worst-case severity across both metrics, plus the booleans the
// rest of the page uses to bucket and badge orgs. Pure function — both KPI
// counts and section membership read from this, so they can never disagree.
function describeOrg(org) {
  const ocr = org.currentUsage?.[OCR_USAGE_KEY] || { count: 0 };
  const pc = org.currentUsage?.[PC_USAGE_KEY] || { count: 0 };
  const effectiveOcr =
    org.aiUsageLimits?.[OCR_KEY] ?? org.systemDefaults?.[OCR_KEY];
  const effectivePc =
    org.aiUsageLimits?.[PC_KEY] ?? org.systemDefaults?.[PC_KEY];
  // Use effective counts (window-aware) so an org that hit cap a week ago
  // and sat idle isn't still flagged "At cap" — its window has expired.
  const ocrCount = getEffectiveQuotaCount(ocr);
  const pcCount = getEffectiveQuotaCount(pc);
  const ocrRatio = effectiveOcr ? ocrCount / effectiveOcr : 0;
  const pcRatio = effectivePc ? pcCount / effectivePc : 0;
  const worstSev = severityForRatio(Math.max(ocrRatio, pcRatio));
  const hasOverride =
    org.aiUsageLimits?.[OCR_KEY] != null ||
    org.aiUsageLimits?.[PC_KEY] != null ||
    org.aiUsageLimits?.[PC_PER_LOAN_KEY] != null ||
    !!org[PC_PER_LOAN_UNLIMITED_KEY];
  return {
    worstSev,
    hasOverride,
    autoRun: !!org.autoRunProcessingChecklist,
    splitting:
      !!org.enableDocumentSplitting ||
      !!org.systemDefaults?.isSplitFlowAllowlisted,
    ocrMode: ocrModeOf(org),
  };
}

// Section predicates. An org can match multiple — e.g. an at-cap auto-run org
// with a custom override appears in "At cap", "Custom overrides", AND
// "Active auto-run". Each section now answers exactly the question its label
// asks. "Quiet" is the catch-all for orgs that match no other section.
const SECTION_PREDICATES = {
  critical: (d) => d.worstSev === "critical",
  watch: (d) => d.worstSev === "watch",
  overrides: (d) => d.hasOverride,
  active: (d) => d.autoRun,
  splitting: (d) => d.splitting,
  ocrPrimary: (d) => d.ocrMode === OCR_MODE_PRIMARY,
  ocrShadow: (d) => d.ocrMode === OCR_MODE_SHADOW,
  quiet: (d) =>
    d.worstSev !== "critical" &&
    d.worstSev !== "watch" &&
    !d.hasOverride &&
    !d.autoRun &&
    !d.splitting &&
    d.ocrMode === OCR_MODE_EXTEND,
};

// ---------- subcomponents ----------

function MetricRow({ label, count, limit, hasOverride }) {
  const safeCount = Number.isFinite(count) ? count : 0;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 1;
  const ratio = safeCount / safeLimit;
  const pct = ratio * 100;
  const sev = severityForRatio(ratio);
  const color = severityColor(sev);
  const isIdle = safeCount === 0;
  // Idle metrics get a near-flat bar so the row still has visual weight but
  // doesn't pull the eye like an active one.
  const countStyle = isIdle ? sx.metricCountDim : sx.metricCount;

  return (
    <div style={sx.metric}>
      <div style={sx.metricLabel}>
        <div style={sx.metricLabelLeft}>
          <span>{label}</span>
          {hasOverride && <span style={sx.customMark}>Custom</span>}
        </div>
        <span style={countStyle}>
          {safeCount.toLocaleString()} /{" "}
          {Number.isFinite(limit) ? limit.toLocaleString() : "-"}
        </span>
      </div>
      <div style={sx.progressTrack}>
        <div style={sx.progressFill(color, pct)} />
      </div>
    </div>
  );
}

// Only show a pill when the org needs attention. Auto-run / manual is implied
// by which section the card sits in, so showing it on every card is noise.
function StatusPill({ severity }) {
  if (severity === "critical") {
    return (
      <span style={sx.pill(palette.danger, palette.dangerSoft)}>At cap</span>
    );
  }
  if (severity === "watch") {
    return <span style={sx.pill(palette.warn, palette.warnSoft)}>Watch</span>;
  }
  return null;
}

function GlobalDefaultsPanel({ env, globalDefaultsPayload, onSaved }) {
  const values = globalDefaultsPayload?.values || {};
  const ocrDefaultValue = values[OCR_KEY];
  const pcDefaultValue = values[PC_KEY];
  const pcPerLoanDefaultValue = values[PC_PER_LOAN_KEY];
  const [ocrInput, setOcrInput] = useState(
    ocrDefaultValue == null ? "" : String(ocrDefaultValue),
  );
  const [pcInput, setPcInput] = useState(
    pcDefaultValue == null ? "" : String(pcDefaultValue),
  );
  const [pcPerLoanInput, setPcPerLoanInput] = useState(
    pcPerLoanDefaultValue == null ? "" : String(pcPerLoanDefaultValue),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOcrInput(ocrDefaultValue == null ? "" : String(ocrDefaultValue));
    setPcInput(pcDefaultValue == null ? "" : String(pcDefaultValue));
    setPcPerLoanInput(
      pcPerLoanDefaultValue == null ? "" : String(pcPerLoanDefaultValue),
    );
  }, [ocrDefaultValue, pcDefaultValue, pcPerLoanDefaultValue]);

  // An empty box means the key has never been saved, so the backend falls back
  // to its built-in constant. That is a valid state, not a validation error:
  // flagging it would let a key nobody has set yet block saving the others.
  const ocrParsed = useMemo(() => parseInputToOverride(ocrInput), [ocrInput]);
  const pcParsed = useMemo(() => parseInputToOverride(pcInput), [pcInput]);
  const pcPerLoanParsed = useMemo(
    () => parseInputToOverride(pcPerLoanInput),
    [pcPerLoanInput],
  );
  const ocrError =
    ocrParsed && typeof ocrParsed === "object" && ocrParsed.error;
  const pcError = pcParsed && typeof pcParsed === "object" && pcParsed.error;
  const pcPerLoanError =
    pcPerLoanParsed &&
    typeof pcPerLoanParsed === "object" &&
    pcPerLoanParsed.error;
  const ocrDirty =
    !ocrError && ocrParsed != null && ocrParsed !== ocrDefaultValue;
  const pcDirty = !pcError && pcParsed != null && pcParsed !== pcDefaultValue;
  const pcPerLoanDirty =
    !pcPerLoanError &&
    pcPerLoanParsed != null &&
    pcPerLoanParsed !== pcPerLoanDefaultValue;
  const dirty = ocrDirty || pcDirty || pcPerLoanDirty;
  const canSave = dirty && !ocrError && !pcError && !pcPerLoanError && !saving;

  // What the backend will use: what is typed, else what is already stored, else
  // nothing is saved and its built-in constant applies.
  const previewValue = (parsed, stored) => {
    if (Number.isFinite(parsed)) {
      return parsed.toLocaleString();
    }
    if (Number.isFinite(stored)) {
      return stored.toLocaleString();
    }

    return "Not set";
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) {
        toast.error("Admin user not found. Please refresh and sign in again.");
        return;
      }

      const body = { userId, globalDefaults: {} };
      if (ocrDirty) body.globalDefaults[OCR_KEY] = ocrParsed;
      if (pcDirty) body.globalDefaults[PC_KEY] = pcParsed;
      if (pcPerLoanDirty) {
        body.globalDefaults[PC_PER_LOAN_KEY] = pcPerLoanParsed;
      }

      const baseUrls = getEnvBackends(env);
      let lastError = "Failed to save global defaults";
      let saved = null;
      for (const baseUrl of baseUrls) {
        try {
          const resp = await fetch(
            `${baseUrl}/admin/organizations/usage-limits/defaults`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            },
          );
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok || !json.success) {
            lastError = json.reason || `Save failed on ${baseUrl}`;
            continue;
          }
          saved = json.globalDefaults;
          break;
        } catch (err) {
          lastError = err?.message || `Could not reach ${baseUrl}`;
        }
      }

      if (!saved) {
        toast.error(lastError);
        return;
      }

      toast.success("Saved global defaults");
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  };

  const updatedText =
    globalDefaultsPayload?.lastUpdatedBy && globalDefaultsPayload?.lastUpdatedAt
      ? `Last edited by ${globalDefaultsPayload.lastUpdatedBy} ${formatRelativeTime(
          globalDefaultsPayload.lastUpdatedAt,
        )}`
      : "Uses code defaults until saved here";
  const envLabel = env === "prod" ? "Production" : "Development";

  return (
    <section style={sx.globalDefaultsPanel}>
      <div style={sx.globalDefaultsHeader}>
        <div>
          <div style={sx.globalDefaultsEyebrow}>
            <span style={sx.kpiDot(palette.accent)} />
            System defaults
          </div>
          <h2 style={sx.globalDefaultsTitle}>Global default limits</h2>
          <p style={sx.globalDefaultsHelp}>
            These values apply to every org without a custom override. NewFi
            keeps its existing special PC auto-run default.
          </p>
        </div>
        <span style={sx.globalDefaultsBadge}>
          <span style={sx.kpiDot(palette.accent)} />
          {envLabel}
        </span>
      </div>
      <div style={sx.globalDefaultsGrid}>
        <div style={sx.globalDefaultTile(palette.danger)}>
          <div style={sx.globalDefaultTileTop}>
            <div>
              <p style={sx.globalDefaultLabel}>Document processing</p>
              <p style={sx.globalDefaultCaption}>
                OCR + classification cap for orgs using the default.
              </p>
            </div>
            <span style={sx.globalDefaultValuePreview}>
              {previewValue(ocrParsed, ocrDefaultValue)}
            </span>
          </div>
          <div style={sx.globalDefaultInputRow}>
            <input
              type="number"
              min="1"
              max="100000"
              placeholder="Not set"
              value={ocrInput}
              onChange={(e) => setOcrInput(e.target.value)}
              style={{
                ...sx.numberInput,
                ...(ocrDirty ? sx.inputDirty : {}),
                ...(ocrError ? sx.inputError : {}),
              }}
            />
            <span style={sx.globalDefaultUnit}>per period</span>
          </div>
          {ocrError && <div style={sx.errorText}>{ocrError}</div>}
        </div>
        <div style={sx.globalDefaultTile(palette.ok)}>
          <div style={sx.globalDefaultTileTop}>
            <div>
              <p style={sx.globalDefaultLabel}>PC auto-runs</p>
              <p style={sx.globalDefaultCaption}>
                Processing-checklist auto-run cap for non-NewFi orgs.
              </p>
            </div>
            <span style={sx.globalDefaultValuePreview}>
              {previewValue(pcParsed, pcDefaultValue)}
            </span>
          </div>
          <div style={sx.globalDefaultInputRow}>
            <input
              type="number"
              min="1"
              max="100000"
              placeholder="Not set"
              value={pcInput}
              onChange={(e) => setPcInput(e.target.value)}
              style={{
                ...sx.numberInput,
                ...(pcDirty ? sx.inputDirty : {}),
                ...(pcError ? sx.inputError : {}),
              }}
            />
            <span style={sx.globalDefaultUnit}>per period</span>
          </div>
          {pcError && <div style={sx.errorText}>{pcError}</div>}
        </div>
        <div style={sx.globalDefaultTile(palette.accent)}>
          <div style={sx.globalDefaultTileTop}>
            <div>
              <p style={sx.globalDefaultLabel}>PC auto-runs per loan</p>
              <p style={sx.globalDefaultCaption}>
                Lifetime cap per loan. Never resets. Manual runs are unaffected.
              </p>
            </div>
            <span style={sx.globalDefaultValuePreview}>
              {previewValue(pcPerLoanParsed, pcPerLoanDefaultValue)}
            </span>
          </div>
          <div style={sx.globalDefaultInputRow}>
            <input
              type="number"
              min="1"
              max="100000"
              placeholder="Not set"
              value={pcPerLoanInput}
              onChange={(e) => setPcPerLoanInput(e.target.value)}
              style={{
                ...sx.numberInput,
                ...(pcPerLoanDirty ? sx.inputDirty : {}),
                ...(pcPerLoanError ? sx.inputError : {}),
              }}
            />
            <span style={sx.globalDefaultUnit}>per loan lifetime</span>
          </div>
          {pcPerLoanError && <div style={sx.errorText}>{pcPerLoanError}</div>}
        </div>
      </div>
      <div style={sx.globalDefaultActions}>
        <span style={sx.metaText}>{updatedText}</span>
        <button
          type="button"
          style={sx.saveBtn(canSave)}
          disabled={!canSave}
          onClick={handleSave}
        >
          {saving ? "Saving..." : dirty ? "Save defaults" : "No changes"}
        </button>
      </div>
    </section>
  );
}

function OrgCard({ org, onClick }) {
  const ocr = org.currentUsage?.[OCR_USAGE_KEY] || {};
  const pc = org.currentUsage?.[PC_USAGE_KEY] || {};
  const effectiveOcr =
    org.aiUsageLimits?.[OCR_KEY] ?? org.systemDefaults?.[OCR_KEY];
  const effectivePc =
    org.aiUsageLimits?.[PC_KEY] ?? org.systemDefaults?.[PC_KEY];
  // Window-aware: see getEffectiveQuotaCount for why we don't trust raw count.
  const ocrCount = getEffectiveQuotaCount(ocr);
  const pcCount = getEffectiveQuotaCount(pc);
  const ocrRatio = effectiveOcr ? ocrCount / effectiveOcr : 0;
  const pcRatio = effectivePc ? pcCount / effectivePc : 0;
  const worstSev = severityForRatio(Math.max(ocrRatio, pcRatio));
  const dotColor = severityColor(worstSev);
  const isNewFi = !!org.systemDefaults?.isNewFi;
  const hasOcrOverride = org.aiUsageLimits?.[OCR_KEY] != null;
  const hasPcOverride = org.aiUsageLimits?.[PC_KEY] != null;
  const hasPcPerLoanOverride = org.aiUsageLimits?.[PC_PER_LOAN_KEY] != null;
  const noPerLoanLimit = !!org[PC_PER_LOAN_UNLIMITED_KEY];
  const hasAnyOverride =
    hasOcrOverride || hasPcOverride || hasPcPerLoanOverride || noPerLoanLimit;
  const autoRunOn = !!org.autoRunProcessingChecklist;
  const fullAutoRunOn = !!org.autoRunFullProcessingChecklist;
  const splitAllowlisted = !!org.systemDefaults?.isSplitFlowAllowlisted;
  const splittingOn = !!org.enableDocumentSplitting || splitAllowlisted;
  const ocrMode = ocrModeOf(org);
  // Extend is the default for ~22k orgs, so badging it would be pure noise.
  // Only the exceptions get a chip.
  const ocrModeOption =
    ocrMode === OCR_MODE_EXTEND ? null : OCR_MODE_BY_ID[ocrMode];

  // Pick the metric with the soonest reset for the single footer countdown —
  // both metrics share the same period in practice, but this is robust.
  const candidates = [ocr.periodEndsAt, pc.periodEndsAt].filter(Boolean);
  const earliestReset = candidates.length ? Math.min(...candidates) : null;
  const resetText = formatTimeRemaining(earliestReset);

  const [hover, setHover] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...sx.card(dotColor),
        boxShadow: hover
          ? "0 8px 20px rgba(15, 23, 42, 0.07)"
          : "0 1px 2px rgba(15, 23, 42, 0.03)",
        borderTopColor: hover ? palette.borderStrong : palette.border,
        borderRightColor: hover ? palette.borderStrong : palette.border,
        borderBottomColor: hover ? palette.borderStrong : palette.border,
      }}
    >
      <div style={sx.cardTopRow}>
        <div style={sx.cardTitleBlock}>
          <h3 style={sx.orgName} title={org.name || org.id}>
            {org.name || org.id}
          </h3>
          <div style={sx.orgMeta} title={org.id}>
            {org.id}
          </div>
        </div>
        <div style={sx.cardPills}>
          <StatusPill severity={worstSev} />
        </div>
      </div>

      {(isNewFi ||
        autoRunOn ||
        fullAutoRunOn ||
        noPerLoanLimit ||
        splittingOn ||
        hasAnyOverride ||
        ocrModeOption) && (
        <div style={sx.cardTags}>
          {ocrModeOption && (
            <span style={sx.chip()} title={ocrModeOption.description}>
              <span
                style={sx.chipDot(ocrModeOption.color)}
                aria-hidden="true"
              />
              {ocrModeOption.short}
            </span>
          )}
          {isNewFi && (
            <span style={sx.chip()} title="Hardcoded NewFi defaults apply">
              <span style={sx.chipDot(palette.newfi)} aria-hidden="true" />
              NewFi
            </span>
          )}
          {autoRunOn && (
            <span style={sx.chip()} title="Auto-run is enabled for this org">
              <span style={sx.chipDot(palette.ok)} aria-hidden="true" />
              Auto-run
            </span>
          )}
          {fullAutoRunOn && (
            <span
              style={sx.chip()}
              title="Automatic runs cover the whole checklist, not just Borrower Approval"
            >
              <span style={sx.chipDot(palette.warn)} aria-hidden="true" />
              Full auto-run
            </span>
          )}
          {noPerLoanLimit && (
            <span
              style={sx.chip()}
              title="The per-loan auto-run cap is switched off for this org"
            >
              <span style={sx.chipDot(palette.warn)} aria-hidden="true" />
              No per-loan cap
            </span>
          )}
          {splittingOn && (
            <span
              style={sx.chip()}
              title={
                splitAllowlisted
                  ? "Document splitting is enabled by hardcoded allowlist"
                  : "Post-classification document splitting is enabled"
              }
            >
              <span style={sx.chipDot(palette.accent)} aria-hidden="true" />
              Splitting
            </span>
          )}
          {hasAnyOverride && (
            <span
              style={sx.chip()}
              title="This org has custom AI usage limits set"
            >
              <span style={sx.chipDot(palette.newfi)} aria-hidden="true" />
              Custom
            </span>
          )}
        </div>
      )}

      <div style={sx.metricGrid}>
        <MetricRow
          label="Doc processing"
          count={ocrCount}
          limit={effectiveOcr}
          hasOverride={hasOcrOverride}
        />
        <MetricRow
          label="PC auto-runs"
          count={pcCount}
          limit={effectivePc}
          hasOverride={hasPcOverride}
        />
      </div>

      {(resetText || org.lastUpdatedBy) && (
        <div style={sx.cardFooter}>
          {resetText || ""}
          {resetText && org.lastUpdatedBy ? " / " : ""}
          {org.lastUpdatedBy
            ? `edited ${formatRelativeTime(org.lastUpdatedAt)}`
            : ""}
        </div>
      )}
    </div>
  );
}

function EditDrawer({
  org,
  env,
  quotaWindowMs,
  ocrRoutingEnv,
  onClose,
  onSaved,
}) {
  const liveOcrOverride = org.aiUsageLimits?.[OCR_KEY] ?? null;
  const livePcOverride = org.aiUsageLimits?.[PC_KEY] ?? null;
  const livePcPerLoanOverride = org.aiUsageLimits?.[PC_PER_LOAN_KEY] ?? null;
  const ocrDefault = org.systemDefaults?.[OCR_KEY];
  const pcDefault = org.systemDefaults?.[PC_KEY];
  const pcPerLoanDefault = org.systemDefaults?.[PC_PER_LOAN_KEY];
  const isNewFi = !!org.systemDefaults?.isNewFi;
  // Wider than isNewFi: also covers the non-prod NewFi orgs.
  const isPcPerLoanExempt = !!org.systemDefaults?.isPcAutoRunPerLoanExempt;
  // NewFi orgs run their own categories, so their automatic runs are never
  // narrowed to Borrower Approval and the toggle below cannot change that.
  const isAutoRunScopeExempt = !!org.systemDefaults?.isAutoRunScopeExempt;
  const splitAllowlisted = !!org.systemDefaults?.isSplitFlowAllowlisted;

  const [ocrInput, setOcrInput] = useState(
    liveOcrOverride == null ? "" : String(liveOcrOverride),
  );
  const [pcInput, setPcInput] = useState(
    livePcOverride == null ? "" : String(livePcOverride),
  );
  const [pcPerLoanInput, setPcPerLoanInput] = useState(
    livePcPerLoanOverride == null ? "" : String(livePcPerLoanOverride),
  );
  const [autoRun, setAutoRun] = useState(!!org.autoRunProcessingChecklist);
  const [fullAutoRun, setFullAutoRun] = useState(
    !!org.autoRunFullProcessingChecklist,
  );
  const [noPerLoanLimit, setNoPerLoanLimit] = useState(
    !!org[PC_PER_LOAN_UNLIMITED_KEY],
  );
  // An org stores only the fields it overrides; the rest inherit the system
  // default. The controls show the resolved value either way. Document lists
  // are global-only, so they are read from the default and shown, not edited.
  const inheritedDocRequirement = (id) =>
    org.systemDefaults?.[DOC_REQUIREMENTS_KEY]?.[id] || {};
  const effectiveDocRequirement = (id) => {
    const override = org[DOC_REQUIREMENTS_KEY]?.[id] || {};
    const inherited = inheritedDocRequirement(id);

    return {
      enabled:
        typeof override.enabled === "boolean"
          ? override.enabled
          : inherited.enabled === true,
      minimumCount: override.minimumCount ?? inherited.minimumCount ?? 1,
    };
  };
  const [docRequirements, setDocRequirements] = useState(() =>
    Object.fromEntries(
      DOC_REQUIREMENTS.map((r) => [r.id, effectiveDocRequirement(r.id)]),
    ),
  );
  const [documentSplitting, setDocumentSplitting] = useState(
    !!org.enableDocumentSplitting,
  );
  const [ocrMode, setOcrMode] = useState(ocrModeOf(org));
  const [engine, setEngine] = useState(engineOf(org, ocrModeOf(org)));
  const engineFamily = engineFamilyOf(ocrMode);
  const engineCopy = ENGINE_COPY[engineFamily];
  const [saving, setSaving] = useState(false);

  const ocrParsed = useMemo(() => parseInputToOverride(ocrInput), [ocrInput]);
  const pcParsed = useMemo(() => parseInputToOverride(pcInput), [pcInput]);
  const pcPerLoanParsed = useMemo(
    () => parseInputToOverride(pcPerLoanInput),
    [pcPerLoanInput],
  );
  const ocrError =
    ocrParsed && typeof ocrParsed === "object" && ocrParsed.error;
  const pcError = pcParsed && typeof pcParsed === "object" && pcParsed.error;
  const pcPerLoanError =
    pcPerLoanParsed &&
    typeof pcPerLoanParsed === "object" &&
    pcPerLoanParsed.error;
  const newOcrOverride = ocrError ? liveOcrOverride : ocrParsed;
  const newPcOverride = pcError ? livePcOverride : pcParsed;
  const newPcPerLoanOverride = pcPerLoanError
    ? livePcPerLoanOverride
    : pcPerLoanParsed;

  const ocrDirty = !ocrError && newOcrOverride !== (liveOcrOverride ?? null);
  const pcDirty = !pcError && newPcOverride !== (livePcOverride ?? null);
  const pcPerLoanDirty =
    !pcPerLoanError && newPcPerLoanOverride !== (livePcPerLoanOverride ?? null);
  const autoRunDirty = autoRun !== !!org.autoRunProcessingChecklist;
  const fullAutoRunDirty = fullAutoRun !== !!org.autoRunFullProcessingChecklist;
  const noPerLoanLimitDirty =
    noPerLoanLimit !== !!org[PC_PER_LOAN_UNLIMITED_KEY];
  const docRequirementsDirty = DOC_REQUIREMENTS.some((r) => {
    const next = docRequirements[r.id];
    const live = effectiveDocRequirement(r.id);

    return (
      next.enabled !== live.enabled || next.minimumCount !== live.minimumCount
    );
  });
  const splittingDirty = documentSplitting !== !!org.enableDocumentSplitting;
  const ocrModeDirty = ocrMode !== ocrModeOf(org);
  const engineDirty = engine !== engineOf(org, ocrMode);
  const dirty =
    ocrDirty ||
    pcDirty ||
    pcPerLoanDirty ||
    autoRunDirty ||
    fullAutoRunDirty ||
    noPerLoanLimitDirty ||
    docRequirementsDirty ||
    splittingDirty ||
    ocrModeDirty ||
    engineDirty;
  const canSave = dirty && !ocrError && !pcError && !pcPerLoanError && !saving;

  // A non-Extend mode can be saved in an environment where the worker cannot
  // act on it. Say so up front rather than letting the org look enabled.
  const ocrModeBlockedReason = (() => {
    if (!ocrRoutingEnv || ocrMode === OCR_MODE_EXTEND) {
      return null;
    }
    if (ocrRoutingEnv.globalEnabled === false) {
      return "Addy OCR is globally disabled in this environment. Documents will keep using Extend.";
    }
    if (ocrRoutingEnv.environmentAllowed === false) {
      return "Non-Extend modes are turned off in this environment. Documents will keep using Extend until it is enabled.";
    }
    if (ocrRoutingEnv.addyConfigured === false) {
      return "Addy OCR has no URL or API key configured in this environment. Documents will keep using Extend.";
    }

    return null;
  })();

  // Esc closes. Trap focus to the drawer for accessibility-lite.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = getAuth()?.currentUser?.uid;
      if (!userId) {
        toast.error("Admin user not found. Please refresh and sign in again.");
        return;
      }

      const body = { userId, organizationId: org.id };
      if (autoRunDirty) body.autoRunProcessingChecklist = autoRun;
      if (fullAutoRunDirty) {
        body.autoRunFullProcessingChecklist = fullAutoRun;
      }
      if (noPerLoanLimitDirty) {
        body[PC_PER_LOAN_UNLIMITED_KEY] = noPerLoanLimit;
      }
      if (docRequirementsDirty) {
        // Send null when a requirement ends up matching the system default so
        // the org keeps inheriting it, rather than pinning a copy that would
        // then ignore any future change to the default.
        const requirements = {};
        for (const r of DOC_REQUIREMENTS) {
          const next = docRequirements[r.id];
          const live = effectiveDocRequirement(r.id);
          if (
            next.enabled === live.enabled &&
            next.minimumCount === live.minimumCount
          ) {
            continue;
          }
          const inherited = inheritedDocRequirement(r.id);
          requirements[r.id] =
            next.enabled === (inherited.enabled === true) &&
            next.minimumCount === (inherited.minimumCount ?? 1)
              ? null
              : { enabled: next.enabled, minimumCount: next.minimumCount };
        }
        body[DOC_REQUIREMENTS_KEY] = requirements;
      }
      if (splittingDirty) body.enableDocumentSplitting = documentSplitting;
      if (ocrModeDirty) body.ocrRouting = { mode: ocrMode };
      if (engineDirty && engineFamily === "extend") body.extendAIEngine = EXTEND_ENGINE_FOR[engine];
      if (engineDirty && engineFamily === "addy") body.addyOcrEngine = ADDY_ENGINE_FOR[engine];
      const limits = {};
      if (ocrDirty) limits[OCR_KEY] = newOcrOverride;
      if (pcDirty) limits[PC_KEY] = newPcOverride;
      if (pcPerLoanDirty) limits[PC_PER_LOAN_KEY] = newPcPerLoanOverride;
      if (Object.keys(limits).length > 0) body.aiUsageLimits = limits;

      const baseUrls = getEnvBackends(env);
      let lastError = "Failed to save";
      let saved = null;
      let savedWarning = null;

      for (const baseUrl of baseUrls) {
        try {
          const resp = await fetch(
            `${baseUrl}/admin/organizations/usage-limits`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            },
          );
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok || !json.success) {
            lastError = json.reason || `Save failed on ${baseUrl}`;
            continue;
          }
          saved = json.organization;
          savedWarning = json.warning || null;
          break;
        } catch (err) {
          lastError = err?.message || `Could not reach ${baseUrl}`;
        }
      }

      if (!saved) {
        toast.error(lastError);
        return;
      }

      toast.success(`Saved ${saved.name || org.id}`);
      // The write succeeded but the backend says it won't take effect here.
      if (savedWarning) {
        toast.warn(savedWarning, { autoClose: 8000 });
      }
      onSaved(saved);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const orgEditUrl = buildOrgEditUrl(env, org.id);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Reflects the pending form state, not the saved state, so the strip always
  // matches what the Save button would write.
  const summaryItems = [
    {
      label: "Auto-run",
      value: autoRun ? "On" : "Off",
      color: autoRun ? palette.ok : palette.textMuted,
    },
    {
      label: "Auto-run scope",
      value: isAutoRunScopeExempt
        ? "Full (NewFi)"
        : fullAutoRun
          ? "Full checklist"
          : "Borrower Approval",
      color: fullAutoRun || isAutoRunScopeExempt ? palette.warn : palette.text,
    },
    {
      label: "Per-loan cap",
      value: isPcPerLoanExempt
        ? "Exempt"
        : noPerLoanLimit
          ? "None"
          : (newPcPerLoanOverride ?? pcPerLoanDefault)?.toLocaleString() || "-",
      color: isPcPerLoanExempt || noPerLoanLimit ? palette.warn : palette.text,
    },
    {
      label: `PC auto-runs ${formatPeriodLabel(quotaWindowMs)}`,
      value: (newPcOverride ?? pcDefault)?.toLocaleString() || "-",
    },
    {
      label: "Doc splitting",
      value: documentSplitting || splitAllowlisted ? "On" : "Off",
      color:
        documentSplitting || splitAllowlisted ? palette.ok : palette.textMuted,
    },
  ];

  const renderUsageBlock = (label, usageKey, effectiveLimit) => {
    const usage = org.currentUsage?.[usageKey] || {};
    // Window-aware count — match the card view, never display a stale at-cap
    // number when the period already expired.
    const count = getEffectiveQuotaCount(usage);
    const safeLimit =
      Number.isFinite(effectiveLimit) && effectiveLimit > 0
        ? effectiveLimit
        : 1;
    const ratio = count / safeLimit;
    const pct = Math.round(ratio * 100);
    const sev = severityForRatio(ratio);
    const color = severityColor(sev);
    const reset = formatTimeRemaining(usage.periodEndsAt);

    return (
      <div style={sx.drawerUsageBlock}>
        <div style={sx.drawerUsageNumbers}>
          <span style={sx.drawerUsageBig}>{count.toLocaleString()}</span>
          <span style={sx.drawerUsageOf}>
            /{" "}
            {Number.isFinite(effectiveLimit)
              ? effectiveLimit.toLocaleString()
              : "-"}{" "}
            {label}
          </span>
          <span style={sx.drawerUsagePct(color)}>{pct}%</span>
        </div>
        <div style={sx.progressTrack}>
          <div style={sx.progressFill(color, pct)} />
        </div>
        <div style={sx.drawerUsageReset}>
          {reset || "No usage in current period"}
        </div>
      </div>
    );
  };

  return (
    <div style={sx.modalBackdrop} onClick={onClose} role="presentation">
      <div
        style={sx.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${org.name || org.id}`}
      >
        <div style={sx.modalHeader}>
          <div style={{ minWidth: 0 }}>
            <h2 style={sx.modalTitle}>
              {org.name || org.id}
              {isNewFi && (
                <span
                  style={{
                    ...sx.pill(palette.newfi, palette.newfiSoft),
                    marginLeft: 8,
                  }}
                >
                  NewFi
                </span>
              )}
            </h2>
            {orgEditUrl ? (
              <a
                href={orgEditUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ ...sx.modalSubtitle, textDecoration: "none" }}
              >
                {org.id} ↗
              </a>
            ) : (
              <p style={sx.modalSubtitle}>{org.id}</p>
            )}
          </div>
          <button
            type="button"
            style={sx.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            <CloseRoundedIcon style={sx.buttonIcon} />
          </button>
        </div>

        <div style={sx.modalSummary}>
          {summaryItems.map((item) => (
            <div key={item.label} style={sx.summaryItem}>
              <p style={sx.summaryLabel}>{item.label}</p>
              <p style={sx.summaryValue(item.color)}>{item.value}</p>
            </div>
          ))}
        </div>

        <div style={sx.modalBody}>
          <div style={sx.modalGrid}>
            <div style={sx.modalColumn}>
              <section style={sx.settingsCard}>
                <div style={sx.settingsCardHead}>
                  <span style={sx.chipDot(palette.accent)} aria-hidden="true" />
                  <h3 style={sx.settingsCardTitle}>Automation</h3>
                </div>

                <div style={sx.fieldGroup}>
                  <div style={sx.toggleRow}>
                    <div>
                      <p style={sx.fieldLabel}>Auto-run processing checklist</p>
                      <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                        When on, new loans are processed automatically as
                        documents arrive.
                      </p>
                    </div>
                    <div
                      style={sx.toggleSwitch(autoRun)}
                      onClick={() => setAutoRun((v) => !v)}
                      role="switch"
                      aria-checked={autoRun}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          setAutoRun((v) => !v);
                        }
                      }}
                    >
                      <div style={sx.toggleKnob(autoRun)} />
                    </div>
                  </div>

                  <div
                    style={{
                      ...sx.nestedFields,
                      ...(autoRun ? {} : sx.nestedFieldsInactive),
                    }}
                  >
                    <div style={sx.toggleRow}>
                      <div>
                        <p style={sx.fieldLabel}>Auto-run the full checklist</p>
                        <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                          Automatic runs normally cover only the Borrower
                          Approval category (Credit, Income, Assets). Turn this
                          on to make them cover every category instead, which
                          uses considerably more AI throughput per run. Manual
                          runs always cover the full checklist either way.
                          {!autoRun && (
                            <>
                              {" "}
                              Auto-run is off for this org, so nothing runs
                              automatically and this setting has no effect yet.
                            </>
                          )}
                          {isAutoRunScopeExempt && (
                            <>
                              {" "}
                              This org already runs the full checklist
                              automatically because it is restricted to its own
                              categories, so this toggle changes nothing for it.
                            </>
                          )}
                        </p>
                      </div>
                      <div
                        style={{
                          ...sx.toggleSwitch(fullAutoRun),
                          ...(autoRun ? {} : { cursor: "not-allowed" }),
                        }}
                        onClick={() => autoRun && setFullAutoRun((v) => !v)}
                        role="switch"
                        aria-checked={fullAutoRun}
                        aria-disabled={!autoRun}
                        tabIndex={autoRun ? 0 : -1}
                        onKeyDown={(e) => {
                          if (autoRun && (e.key === " " || e.key === "Enter")) {
                            e.preventDefault();
                            setFullAutoRun((v) => !v);
                          }
                        }}
                      >
                        <div style={sx.toggleKnob(fullAutoRun)} />
                      </div>
                    </div>

                    {(() => {
                      // The gate only runs on automatic runs that were narrowed
                      // to Borrower Approval, so a full auto-run or a
                      // scope-exempt org never reaches it.
                      const gateApplies =
                        autoRun && !fullAutoRun && !isAutoRunScopeExempt;

                      return (
                        <div
                          style={{
                            ...sx.nestedFields,
                            ...(gateApplies ? {} : sx.nestedFieldsInactive),
                          }}
                        >
                          <p style={sx.fieldLabel}>
                            Required documents before a task runs
                          </p>
                          <p style={sx.fieldHelp}>
                            A loan gets a limited number of automatic runs. When
                            a requirement is on, a run skips that task unless
                            the loan already has the documents it reads, so the
                            loan keeps the slot for when they arrive.
                            {!gateApplies && (
                              <>
                                {" "}
                                {isAutoRunScopeExempt
                                  ? "This org always auto-runs the full checklist, so these have no effect."
                                  : !autoRun
                                    ? "Auto-run is off for this org, so these have no effect yet."
                                    : "Full-checklist auto-runs are on, so these have no effect."}
                              </>
                            )}
                          </p>

                          {DOC_REQUIREMENTS.map((requirement) => {
                            const current = docRequirements[requirement.id];
                            const isOn = !!current.enabled;
                            const documentTypes =
                              inheritedDocRequirement(requirement.id)
                                .documentTypes || [];
                            const patch = (changes) =>
                              gateApplies &&
                              setDocRequirements((prev) => ({
                                ...prev,
                                [requirement.id]: {
                                  ...prev[requirement.id],
                                  ...changes,
                                },
                              }));
                            const toggle = () => patch({ enabled: !isOn });

                            return (
                              <div key={requirement.id} style={sx.toggleRow}>
                                <div>
                                  <p style={sx.fieldLabel}>
                                    {requirement.label} needs{" "}
                                    <input
                                      type="number"
                                      min="1"
                                      max={MAX_MINIMUM_COUNT}
                                      value={current.minimumCount}
                                      disabled={!gateApplies || !isOn}
                                      onChange={(e) =>
                                        patch({
                                          minimumCount: Math.min(
                                            MAX_MINIMUM_COUNT,
                                            Math.max(
                                              1,
                                              Number(e.target.value) || 1,
                                            ),
                                          ),
                                        })
                                      }
                                      style={sx.inlineCountInput}
                                    />{" "}
                                    document
                                    {current.minimumCount === 1 ? "" : "s"}
                                  </p>
                                  <p
                                    style={{
                                      ...sx.fieldHelp,
                                      marginBottom: 0,
                                    }}
                                  >
                                    {requirement.help} Counts{" "}
                                    {documentTypes.length} document type
                                    {documentTypes.length === 1 ? "" : "s"}
                                    {documentTypes.length > 0 && (
                                      <>
                                        {" "}
                                        ({documentTypes.slice(0, 3).join(", ")}
                                        {documentTypes.length > 3 ? ", …" : ""})
                                      </>
                                    )}
                                    , set globally under System defaults.
                                  </p>
                                </div>
                                <div
                                  style={{
                                    ...sx.toggleSwitch(isOn),
                                    ...(gateApplies
                                      ? {}
                                      : { cursor: "not-allowed" }),
                                  }}
                                  onClick={toggle}
                                  role="switch"
                                  aria-checked={isOn}
                                  aria-disabled={!gateApplies}
                                  aria-label={`${requirement.label} minimum documents`}
                                  tabIndex={gateApplies ? 0 : -1}
                                  onKeyDown={(e) => {
                                    if (e.key === " " || e.key === "Enter") {
                                      e.preventDefault();
                                      toggle();
                                    }
                                  }}
                                >
                                  <div style={sx.toggleKnob(isOn)} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div style={sx.fieldGroupLast}>
                  <div style={sx.toggleRow}>
                    <div>
                      <p style={sx.fieldLabel}>Document splitting</p>
                      <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                        When on, multi-document bundles can be split after
                        classification when multiple document types are
                        detected.
                        {splitAllowlisted && (
                          <>
                            {" "}
                            This org is also enabled by the hardcoded split-flow
                            allowlist, so turning this field off will not fully
                            disable splitting until the allowlist is removed.
                          </>
                        )}
                      </p>
                    </div>
                    <div
                      style={sx.toggleSwitch(documentSplitting)}
                      onClick={() => setDocumentSplitting((v) => !v)}
                      role="switch"
                      aria-checked={documentSplitting}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          setDocumentSplitting((v) => !v);
                        }
                      }}
                    >
                      <div style={sx.toggleKnob(documentSplitting)} />
                    </div>
                  </div>
                </div>
              </section>

              <section style={sx.settingsCard}>
                <div style={sx.settingsCardHead}>
                  <span style={sx.chipDot(palette.ok)} aria-hidden="true" />
                  <h3 style={sx.settingsCardTitle}>Document intake</h3>
                </div>

                <div style={sx.fieldGroup}>
                  <p style={sx.fieldLabel}>Document OCR engine</p>
                  <p style={sx.fieldHelp}>
                    Which engine extracts text from this organization&apos;s
                    documents. Applies to the{" "}
                    {env === "dev" ? "development" : "production"} environment
                    only.
                  </p>
                  <div style={sx.ocrModeList}>
                    {OCR_MODE_OPTIONS.map((option) => {
                      const selected = ocrMode === option.id;
                      return (
                        <label
                          key={option.id}
                          style={sx.ocrModeOption(selected, option.color)}
                        >
                          <input
                            type="radio"
                            name="ocrRoutingMode"
                            value={option.id}
                            checked={selected}
                            onChange={() => {
                              setOcrMode(option.id);
                              setEngine(engineOf(org, option.id));
                            }}
                            style={sx.ocrModeRadio}
                          />
                          <span>
                            <span style={sx.ocrModeLabel(option.color)}>
                              {option.label}
                            </span>
                            <span style={sx.ocrModeDescription}>
                              {option.description}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {ocrModeBlockedReason && (
                    <p style={sx.ocrModeWarning}>{ocrModeBlockedReason}</p>
                  )}
                  {org.ocrRouting?.updatedBy && (
                    <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                      Engine last changed by {org.ocrRouting.updatedBy}{" "}
                      {formatRelativeTime(org.ocrRouting.updatedAt)}.
                    </p>
                  )}
                </div>

                <div style={sx.fieldGroupLast}>
                  <p style={sx.fieldLabel}>{engineCopy.label}</p>
                  <p style={sx.fieldHelp}>
                    {engineCopy.help} Follows the engine chosen above; takes
                    effect on the next document.
                  </p>
                  <div style={sx.ocrModeList}>
                    {[
                      { id: ENGINE_PERFORMANCE, label: "Performance", color: "#0891b2" },
                      { id: ENGINE_LIGHT, label: "Light", color: "#7c3aed" },
                    ].map((option) => {
                      const selected = engine === option.id;
                      return (
                        <label
                          key={option.id}
                          style={sx.ocrModeOption(selected, option.color)}
                        >
                          <input
                            type="radio"
                            name="parsingEngine"
                            value={option.id}
                            checked={selected}
                            onChange={() => setEngine(option.id)}
                            style={sx.ocrModeRadio}
                          />
                          <span>
                            <span style={sx.ocrModeLabel(option.color)}>
                              {option.label}
                            </span>
                            <span style={sx.ocrModeDescription}>
                              {engineCopy[option.id]}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </section>
            </div>

            <div style={sx.modalColumn}>
              <section style={sx.settingsCard}>
                <div style={sx.settingsCardHead}>
                  <span style={sx.chipDot(palette.danger)} aria-hidden="true" />
                  <h3 style={sx.settingsCardTitle}>AI usage limits</h3>
                </div>

                <div style={sx.fieldGroup}>
                  <p style={sx.fieldLabel}>
                    Document processing limit (
                    {formatPeriodLabel(quotaWindowMs)})
                  </p>
                  <p style={sx.fieldHelp}>
                    Caps OCR &amp; classification calls. System default:{" "}
                    <strong>{ocrDefault?.toLocaleString() ?? "-"}</strong>.
                  </p>
                  {renderUsageBlock(
                    "used",
                    OCR_USAGE_KEY,
                    newOcrOverride ?? ocrDefault,
                  )}
                  <div style={sx.overrideRow}>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      placeholder={`Default: ${ocrDefault ?? "-"}`}
                      value={ocrInput}
                      onChange={(e) => setOcrInput(e.target.value)}
                      style={{
                        ...sx.numberInput,
                        ...(ocrDirty ? sx.inputDirty : {}),
                        ...(ocrError ? sx.inputError : {}),
                      }}
                    />
                    {liveOcrOverride != null && (
                      <button
                        type="button"
                        style={sx.defaultBtn}
                        onClick={() => setOcrInput("")}
                        title="Clear the override and use the system default"
                      >
                        Use default
                      </button>
                    )}
                  </div>
                  {ocrError && <div style={sx.errorText}>{ocrError}</div>}
                </div>

                <div style={sx.fieldGroup}>
                  <p style={sx.fieldLabel}>
                    PC auto-runs limit ({formatPeriodLabel(quotaWindowMs)})
                  </p>
                  <p style={sx.fieldHelp}>
                    Caps how many processing-checklist auto-runs fire per
                    period. System default:{" "}
                    <strong>
                      {pcDefault?.toLocaleString() ?? "-"}
                      {isNewFi ? " (NewFi)" : ""}
                    </strong>
                    .
                  </p>
                  {renderUsageBlock(
                    "used",
                    PC_USAGE_KEY,
                    newPcOverride ?? pcDefault,
                  )}
                  <div style={sx.overrideRow}>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      placeholder={`Default: ${pcDefault ?? "-"}`}
                      value={pcInput}
                      onChange={(e) => setPcInput(e.target.value)}
                      style={{
                        ...sx.numberInput,
                        ...(pcDirty ? sx.inputDirty : {}),
                        ...(pcError ? sx.inputError : {}),
                      }}
                    />
                    {livePcOverride != null && (
                      <button
                        type="button"
                        style={sx.defaultBtn}
                        onClick={() => setPcInput("")}
                        title="Clear the override and use the system default"
                      >
                        Use default
                      </button>
                    )}
                  </div>
                  {pcError && <div style={sx.errorText}>{pcError}</div>}
                </div>

                <div style={sx.fieldGroup}>
                  <p style={sx.fieldLabel}>
                    PC auto-runs limit (per loan lifetime)
                  </p>
                  <p style={sx.fieldHelp}>
                    Caps how many times a single loan can ever be auto-run. This
                    one never resets, so once a loan hits the cap only a manual
                    run will refresh it. Manual runs never count against it.{" "}
                    {isPcPerLoanExempt ? (
                      <strong>This org is exempt from this cap.</strong>
                    ) : noPerLoanLimit ? (
                      <strong>This cap is switched off for this org.</strong>
                    ) : (
                      <>
                        System default:{" "}
                        <strong>
                          {pcPerLoanDefault?.toLocaleString() ?? "-"}
                        </strong>
                        .
                      </>
                    )}
                  </p>
                  <div style={sx.overrideRow}>
                    <input
                      type="number"
                      min="1"
                      max="100000"
                      placeholder={`Default: ${pcPerLoanDefault ?? "-"}`}
                      value={pcPerLoanInput}
                      onChange={(e) => setPcPerLoanInput(e.target.value)}
                      disabled={noPerLoanLimit || isPcPerLoanExempt}
                      style={{
                        ...sx.numberInput,
                        ...(pcPerLoanDirty ? sx.inputDirty : {}),
                        ...(pcPerLoanError ? sx.inputError : {}),
                        ...(noPerLoanLimit || isPcPerLoanExempt
                          ? { opacity: 0.5, cursor: "not-allowed" }
                          : {}),
                      }}
                    />
                    {livePcPerLoanOverride != null && (
                      <button
                        type="button"
                        style={sx.defaultBtn}
                        onClick={() => setPcPerLoanInput("")}
                        title="Clear the override and use the system default"
                      >
                        Use default
                      </button>
                    )}
                  </div>
                  {pcPerLoanError && (
                    <div style={sx.errorText}>{pcPerLoanError}</div>
                  )}
                  {!isPcPerLoanExempt && (
                    <div style={{ ...sx.nestedFields, ...sx.toggleRow }}>
                      <div>
                        <p style={sx.fieldLabel}>No per-loan cap</p>
                        <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                          Removes the cap entirely for this org, so a loan can
                          be auto-run as often as the org&apos;s daily limit
                          allows. Use this instead of entering a very large
                          number. It wins over the number above, whether that
                          number is an override or the system default.
                        </p>
                      </div>
                      <div
                        style={sx.toggleSwitch(noPerLoanLimit)}
                        onClick={() => setNoPerLoanLimit((v) => !v)}
                        role="switch"
                        aria-checked={noPerLoanLimit}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            setNoPerLoanLimit((v) => !v);
                          }
                        }}
                      >
                        <div style={sx.toggleKnob(noPerLoanLimit)} />
                      </div>
                    </div>
                  )}
                </div>

                <div style={sx.fieldGroupLast}>
                  <p style={{ ...sx.fieldHelp, marginBottom: 0 }}>
                    Quota window (
                    {formatPeriodLabel(quotaWindowMs).replace("per ", "")}) is
                    set in code, not editable here. Persisted usage history
                    isn't tracked yet — the numbers above reflect the current
                    period only.
                    {org.lastUpdatedBy && (
                      <>
                        <br />
                        <br />
                        Last edited by <strong>{org.lastUpdatedBy}</strong>{" "}
                        {formatRelativeTime(org.lastUpdatedAt)}.
                      </>
                    )}
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>

        <div style={sx.modalFooter}>
          <button type="button" style={sx.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            style={sx.saveBtn(canSave)}
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving ? "Saving..." : dirty ? "Save changes" : "No changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- main page ----------

function OrganizationLimitsPage({ onReady }) {
  const [env, setEnv] = useState("prod");
  const [orgs, setOrgs] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [quotaWindowMs, setQuotaWindowMs] = useState(null);
  const [globalDefaultsPayload, setGlobalDefaultsPayload] = useState(null);
  const [ocrRoutingEnv, setOcrRoutingEnv] = useState(null);
  const [serverEnv, setServerEnv] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(null); // null | group id
  const [drawerOrgId, setDrawerOrgId] = useState(null);
  // Stable identity: getEnvBackends builds a new array each call, and a child
  // that fetches on this list would re-fetch on every render.
  const envBackends = useMemo(() => getEnvBackends(env), [env]);

  // Allow deep-links (e.g. from the Org 360 dashboard) to land pre-filtered to a
  // single organization: /organization-limits?org=<id>&env=dev
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orgParam = params.get("org") || params.get("organizationId");
    const envParam = params.get("env");
    if (envParam === "dev" || envParam === "prod") setEnv(envParam);
    if (orgParam) {
      setSearchInput(orgParam.trim());
      setDebouncedSearch(orgParam.trim());
    }
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Force a re-render every 30s so the "resets in Xm" labels stay accurate
  // without users having to hit refresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const buildUrl = useCallback(
    (baseUrl, userId, { cursor } = {}) => {
      const url = new URL(`${baseUrl}/admin/organizations/usage-limits`);
      url.searchParams.set("userId", userId);
      if (debouncedSearch) {
        url.searchParams.set("search", debouncedSearch);
      } else {
        url.searchParams.set("limit", String(PAGE_SIZE));
        if (cursor) url.searchParams.set("cursor", cursor);
      }
      return url.toString();
    },
    [debouncedSearch],
  );

  const fetchPage = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setIsLoadingMore(true);
      else setIsLoading(true);
      try {
        const userId = getAuth()?.currentUser?.uid;
        if (!userId) {
          throw new Error(
            "Admin user not found. Please refresh and sign in again.",
          );
        }

        const baseUrls = getEnvBackends(env);
        let lastError = `Failed to load ${env} usage limits`;
        let loaded = false;

        for (const baseUrl of baseUrls) {
          try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 60000);
            const resp = await fetch(buildUrl(baseUrl, userId, { cursor }), {
              signal: controller.signal,
            });
            clearTimeout(timeout);
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok || !json.success) {
              lastError = json.reason || `Failed on ${baseUrl}`;
              continue;
            }
            const incoming = Array.isArray(json.orgs) ? json.orgs : [];
            setOrgs((prev) => {
              if (!append) return incoming;
              // Toggle-enabled orgs are pinned on page 1 and may also appear
              // in later alphabetical pages, so dedupe by id.
              const seen = new Set(prev.map((o) => o.id));
              const fresh = incoming.filter((o) => !seen.has(o.id));
              return [...prev, ...fresh];
            });
            setNextCursor(json.nextCursor || null);
            setQuotaWindowMs(json.quotaWindowMs || null);
            setGlobalDefaultsPayload(json.globalDefaults || null);
            setOcrRoutingEnv(json.ocrRoutingEnv || null);
            setServerEnv(json.env || null);
            loaded = true;
            break;
          } catch (err) {
            lastError =
              err?.name === "AbortError"
                ? `Timed out at ${baseUrl}`
                : err?.message || `Could not reach ${baseUrl}`;
          }
        }

        if (!loaded) throw new Error(lastError);
      } catch (error) {
        console.error("Failed to load org usage limits:", error);
        toast.error(error.message || "Failed to load org usage limits");
        if (!append) {
          setOrgs([]);
          setNextCursor(null);
        }
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        if (onReady) onReady();
      }
    },
    [env, buildUrl, onReady],
  );

  useEffect(() => {
    fetchPage({ cursor: null, append: false });
  }, [fetchPage]);

  const handleSaved = useCallback((saved) => {
    setOrgs((prev) =>
      prev.map((o) => (o.id === saved.id ? { ...o, ...saved } : o)),
    );
  }, []);

  const handleGlobalDefaultsSaved = useCallback((payload) => {
    setGlobalDefaultsPayload(payload || null);
    const values = payload?.values || {};
    const nextOcr = values[OCR_KEY];
    const nextPc = values[PC_KEY];
    const nextPcPerLoan = values[PC_PER_LOAN_KEY];
    setOrgs((prev) =>
      prev.map((org) => {
        const systemDefaults = { ...(org.systemDefaults || {}) };
        if (Number.isFinite(nextOcr)) {
          systemDefaults[OCR_KEY] = nextOcr;
        }
        if (Number.isFinite(nextPc) && !systemDefaults.isNewFi) {
          systemDefaults[PC_KEY] = nextPc;
        }
        // Exempt orgs have no per-loan cap, so the default never applies.
        if (
          Number.isFinite(nextPcPerLoan) &&
          !systemDefaults.isPcAutoRunPerLoanExempt
        ) {
          systemDefaults[PC_PER_LOAN_KEY] = nextPcPerLoan;
        }
        return { ...org, systemDefaults };
      }),
    );
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!nextCursor || isLoadingMore) return;
    fetchPage({ cursor: nextCursor, append: true });
  }, [nextCursor, isLoadingMore, fetchPage]);

  // Compute descriptors + section membership in a single pass. Predicates
  // mean an org can land in multiple sections (e.g. at-cap & auto-run). KPI
  // counts share the predicates so section length === KPI count by construction.
  const { buckets, counts, describedOrgs } = useMemo(() => {
    const desc = orgs.map((o) => ({ org: o, ...describeOrg(o) }));
    const out = {
      critical: [],
      watch: [],
      overrides: [],
      active: [],
      splitting: [],
      ocrPrimary: [],
      ocrShadow: [],
      quiet: [],
    };
    for (const d of desc) {
      for (const k of Object.keys(SECTION_PREDICATES)) {
        if (SECTION_PREDICATES[k](d)) out[k].push(d.org);
      }
    }
    const byName = (a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      });
    for (const k of Object.keys(out)) out[k].sort(byName);
    // KPI counts mirror section lengths. "quiet" isn't surfaced as a KPI.
    const c = {
      critical: out.critical.length,
      watch: out.watch.length,
      overrides: out.overrides.length,
      active: out.active.length,
      splitting: out.splitting.length,
      ocrPrimary: out.ocrPrimary.length,
      ocrShadow: out.ocrShadow.length,
    };
    return { buckets: out, counts: c, describedOrgs: desc };
  }, [orgs]);

  const drawerOrg = useMemo(
    () => orgs.find((o) => o.id === drawerOrgId) || null,
    [orgs, drawerOrgId],
  );

  const periodLabel = formatPeriodLabel(quotaWindowMs);
  const searchPending =
    searchInput.trim() !== "" && searchInput.trim() !== debouncedSearch;
  const isSearching = !!debouncedSearch;
  const showLoadMore = !isSearching && !!nextCursor;
  const totalLoaded = orgs.length;

  // KPI scan state:
  // - "active" and "splitting" are globally accurate because the backend pins
  //   every org with either toggle on to page 1, so these counts over the
  //   loaded set are complete regardless of how many orgs we've paged through.
  // - critical/watch/overrides only see what we've loaded so far. The number
  //   shown is real, but to be honest we suffix it with "+" while there are
  //   more orgs to scan, and drop the "+" once `scannedAll` is true.
  // - Click "Show more" to keep loading 1k orgs at a time and watch the
  //   numbers tighten up; eventually scannedAll == true and they're final.
  const scannedAll = !nextCursor;
  const kpis = [
    {
      id: "critical",
      label: "At cap right now",
      value: counts.critical,
      color: palette.danger,
      sub: "Hit limit on OCR or PC",
      partial: !scannedAll,
    },
    {
      id: "watch",
      label: "Watch list",
      value: counts.watch,
      color: palette.warn,
      sub: "70%+ of either limit",
      partial: !scannedAll,
    },
    {
      id: "overrides",
      label: "Custom overrides",
      value: counts.overrides,
      color: palette.newfi,
      sub: "Per-org limit configured",
      partial: !scannedAll,
    },
    {
      id: "active",
      label: "Active auto-run",
      value: counts.active,
      color: palette.ok,
      sub: "Auto-run toggle is on",
      // Always complete because backend pins all auto-run orgs to page 1.
      partial: false,
    },
    {
      id: "splitting",
      label: "Document splitting",
      value: counts.splitting,
      color: palette.accent,
      sub: "Split flow toggle is on",
      // Always complete because backend pins all splitting-enabled orgs to page 1.
      partial: false,
    },
    {
      id: "ocrPrimary",
      label: "Addy OCR primary",
      value: counts.ocrPrimary,
      color: OCR_MODE_BY_ID[OCR_MODE_PRIMARY].color,
      sub: "Addy OCR runs first",
      // Always complete because the backend pins every org with a non-Extend
      // OCR mode to page 1.
      partial: false,
    },
    {
      id: "ocrShadow",
      label: "OCR shadow",
      value: counts.ocrShadow,
      color: OCR_MODE_BY_ID[OCR_MODE_SHADOW].color,
      sub: "Both engines run",
      partial: false,
    },
    {
      id: "window",
      label: "Quota window",
      value: periodLabel.replace("per ", ""),
      color: palette.accent,
      sub: serverEnv === "dev" ? "Dev environment" : "Production",
      noFilter: true,
      isText: true,
    },
  ];

  // Search short-circuits everything → flat list. With a KPI filter active we
  // collapse to that one section's flat list (the same orgs, but isolated so
  // the user can focus). Default view: the full priority-grouped sections.
  const sevRank = { critical: 0, watch: 1, ok: 2, idle: 3 };
  let visibleSections;
  if (isSearching) {
    visibleSections = [
      {
        group: {
          id: "search",
          label: `Results for "${debouncedSearch}"`,
          description: "",
          color: palette.accent,
          soft: palette.accentSoft,
        },
        orgs,
      },
    ];
  } else if (activeFilter && SECTION_PREDICATES[activeFilter]) {
    const filtered = describedOrgs
      .filter(SECTION_PREDICATES[activeFilter])
      .sort((a, b) => {
        const r = (sevRank[a.worstSev] ?? 9) - (sevRank[b.worstSev] ?? 9);
        if (r !== 0) return r;
        return (a.org.name || "").localeCompare(b.org.name || "", undefined, {
          sensitivity: "base",
        });
      })
      .map((d) => d.org);
    const matching = GROUPS.find((g) => g.id === activeFilter) || {
      id: activeFilter,
      label: activeFilter,
      description: "",
      color: palette.accent,
      soft: palette.accentSoft,
    };
    visibleSections = [{ group: matching, orgs: filtered }];
  } else {
    visibleSections = GROUPS.filter((g) => buckets[g.id].length > 0).map(
      (g) => ({ group: g, orgs: buckets[g.id] }),
    );
  }

  // With overlapping sections, summing visibleSections double-counts. Show the
  // unique-org count to match what the user would think of as "loaded".
  const visibleUniqueIds = new Set();
  for (const s of visibleSections)
    for (const o of s.orgs) visibleUniqueIds.add(o.id);
  const totalVisible = visibleUniqueIds.size;

  return (
    <div className="OrganizationLimitsPageContainer" style={sx.page}>
      <ToastContainer position="top-right" autoClose={3500} />

      {/* keyframes — injected once. Inline styles can't define them. */}
      <style>{`
        @keyframes olPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes olFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes olModalIn { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: none; } }
      `}</style>

      <div style={sx.header}>
        <div>
          <h1 style={sx.title}>Organization Limits</h1>
          <p style={sx.subtitle}>
            Per-org auto-run toggle and AI hard caps ({periodLabel}).
            {serverEnv && (
              <span style={sx.envBadge}>
                <span style={sx.kpiDot(palette.accent)} />
                {serverEnv === "prod" ? "Production" : "Development"}
              </span>
            )}
          </p>
        </div>
        <div style={sx.controls}>
          <div style={sx.envSegment}>
            <button
              type="button"
              style={sx.envButton(env === "prod")}
              onClick={() => setEnv("prod")}
            >
              Production
            </button>
            <button
              type="button"
              style={sx.envButton(env === "dev")}
              onClick={() => setEnv("dev")}
            >
              Development
            </button>
          </div>
          <button
            type="button"
            style={sx.refreshBtn}
            onClick={() => fetchPage({ cursor: null, append: false })}
            disabled={isLoading}
          >
            <RefreshRoundedIcon style={sx.buttonIcon} />
            {isLoading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* KPI hero strip — first 4 are clickable filters, last (Quota window)
          is informational only. */}
      <div style={sx.kpiGrid}>
        {kpis.map((k) => {
          const isActive = activeFilter === k.id;
          const clickable = !k.noFilter;
          return (
            <button
              key={k.id}
              type="button"
              style={{
                ...sx.kpiCard(isActive, k.color),
                cursor: clickable ? "pointer" : "default",
              }}
              onClick={() => {
                if (!clickable) return;
                setActiveFilter(isActive ? null : k.id);
              }}
              aria-pressed={isActive}
              title={
                clickable
                  ? isActive
                    ? "Click to clear filter"
                    : `Filter to "${k.label}"`
                  : undefined
              }
            >
              <div style={sx.kpiLabel}>
                <span style={sx.kpiDot(k.color)} />
                {k.label}
              </div>
              <div style={sx.kpiValue}>
                {k.isText ? k.value : (k.value || 0).toLocaleString()}
                {/* "+" while partial → reminds users this can grow. */}
                {!k.isText && k.partial && (
                  <span style={sx.kpiPartialBadge} title="More orgs unscanned">
                    +
                  </span>
                )}
              </div>
              <div style={sx.kpiSubtext}>{k.sub}</div>
            </button>
          );
        })}
      </div>

      <GlobalOcrSwitchPanel env={env} backends={getEnvBackends(env)} />

      {globalDefaultsPayload && (
        <GlobalDefaultsPanel
          env={env}
          globalDefaultsPayload={globalDefaultsPayload}
          onSaved={handleGlobalDefaultsSaved}
        />
      )}

      {globalDefaultsPayload && (
        <MinimumDocumentsPanel
          env={env}
          baseUrls={envBackends}
          requirements={globalDefaultsPayload[DOC_REQUIREMENTS_KEY]}
          onSaved={handleGlobalDefaultsSaved}
        />
      )}

      <div style={sx.searchRow}>
        <div style={sx.searchWrap}>
          <SearchRoundedIcon style={sx.searchIcon} aria-hidden="true" />
          <input
            type="text"
            placeholder="Search any org by name (case-insensitive prefix) or paste exact ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={sx.searchInput}
          />
          {searchInput && (
            <button
              type="button"
              style={sx.searchClear}
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
              title="Clear search"
            >
              <CloseRoundedIcon style={sx.buttonIcon} />
            </button>
          )}
        </div>
        <span style={sx.countText}>
          {isSearching ? (
            <>
              {totalVisible} match{totalVisible === 1 ? "" : "es"}
            </>
          ) : (
            <>
              {/* Page-level scan progress. Honest about what we have:
                  - Default view: "X loaded · all scanned" or "X loaded · scanning…"
                  - Filter view:  "X matches in Y loaded · scanning…" */}
              {activeFilter ? (
                <>
                  <strong>{totalVisible.toLocaleString()}</strong> match
                  {totalVisible === 1 ? "" : "es"} in{" "}
                  {totalLoaded.toLocaleString()} loaded
                </>
              ) : (
                <>
                  <strong>{totalLoaded.toLocaleString()}</strong> orgs loaded
                </>
              )}
              {nextCursor ? (
                <span style={sx.scanHint}> - more orgs not yet scanned</span>
              ) : (
                <span style={sx.scanHintDone}> - all orgs scanned</span>
              )}
            </>
          )}
          {searchPending && <span style={sx.spinnerDot} />}
          {activeFilter && !isSearching && (
            <button
              type="button"
              style={sx.resetFilterBtn}
              onClick={() => setActiveFilter(null)}
            >
              Clear filter
            </button>
          )}
        </span>
      </div>

      {isLoading ? (
        <div style={sx.loading}>
          {isSearching
            ? `Searching for "${debouncedSearch}"...`
            : "Loading organizations..."}
        </div>
      ) : totalVisible === 0 ? (
        <div style={sx.empty}>
          {isSearching
            ? `No organizations match "${debouncedSearch}". Try a different prefix or paste the exact org ID.`
            : activeFilter
              ? `No organizations in this group. Clear the filter to see everything else.`
              : "No organizations loaded yet."}
        </div>
      ) : (
        visibleSections.map(({ group, orgs: bucketOrgs }) => (
          <SectionView
            key={group.id}
            group={group}
            orgs={bucketOrgs}
            onCardClick={(id) => setDrawerOrgId(id)}
          />
        ))
      )}

      {showLoadMore && !isLoading && (
        <button
          type="button"
          style={sx.loadMoreBtn(!isLoadingMore)}
          onClick={handleLoadMore}
          disabled={isLoadingMore}
          title={
            activeFilter
              ? `Scan another ${PAGE_SIZE.toLocaleString()} orgs to find more matches in this filter`
              : `Load the next ${PAGE_SIZE.toLocaleString()} orgs alphabetically`
          }
        >
          {isLoadingMore
            ? "Loading more..."
            : activeFilter
              ? `Scan ${PAGE_SIZE.toLocaleString()} more orgs to find more matches`
              : `Load ${PAGE_SIZE.toLocaleString()} more orgs - ${totalLoaded.toLocaleString()} loaded so far`}
        </button>
      )}

      {drawerOrg && (
        <EditDrawer
          org={drawerOrg}
          env={env}
          quotaWindowMs={quotaWindowMs}
          ocrRoutingEnv={ocrRoutingEnv}
          onClose={() => setDrawerOrgId(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function SectionView({ group, orgs, onCardClick }) {
  return (
    <section>
      <header style={sx.sectionHeader()}>
        <span style={sx.sectionMark(group.color)} aria-hidden="true" />
        <span style={sx.sectionText}>
          <span style={sx.sectionTitle}>{group.label}</span>
          {group.description && (
            <span style={sx.sectionDescription}>{group.description}</span>
          )}
        </span>
        <span style={sx.sectionCount}>{orgs.length}</span>
      </header>
      <div style={sx.cardGrid}>
        {orgs.map((org) => (
          <OrgCard key={org.id} org={org} onClick={() => onCardClick(org.id)} />
        ))}
      </div>
    </section>
  );
}

export default OrganizationLimitsPage;
